import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import ChatInterface from './ChatInterface';
import chatService from '../../services/chatService';
import { notification } from 'antd';

const Messages = () => {
  const { user, loading } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const navigate = useNavigate();

  // Gestion des erreurs
  const showError = (message) => {
    notification.error({
      message: 'Erreur',
      description: message,
      placement: 'topRight',
    });
  };

  useEffect(() => {
    let isMounted = true;
    
    const initializeChat = async () => {
      if (!user?.token) {
        console.warn('[Messages] Aucun token utilisateur disponible');
        return;
      }

      console.log('[Messages] Initialisation de la connexion WebSocket...');
      
      try {
        // Gestionnaires d'événements
        const onConnect = () => {
          console.log('[Messages] Connexion WebSocket établie avec succès');
          if (isMounted) setIsConnected(true);
        };
        
        const onError = (error) => {
          console.error('[Messages] Erreur WebSocket:', error);
          if (isMounted) setIsConnected(false);
          showError('Erreur de connexion au chat. Veuillez rafraîchir la page.');
        };
        
        const onDisconnect = (event) => {
          console.warn('[Messages] Déconnexion WebSocket:', event);
          if (isMounted) setIsConnected(false);
          
          // Tentative de reconnexion si la déconnexion n'était pas intentionnelle
          if (event.code !== 1000) {
            console.log('[Messages] Tentative de reconnexion...');
            setTimeout(() => initializeChat(), 3000);
          }
        };
        
        const onMessage = (message) => {
          console.log('[Messages] Nouveau message reçu:', message);
          // Émettre un événement personnalisé pour notifier les composants enfants
          // que de nouveaux messages sont disponibles
          const event = new CustomEvent('newMessage', { detail: message });
          window.dispatchEvent(event);
        };
        
        // S'abonner aux événements
        chatService.on('connect', onConnect);
        chatService.on('error', onError);
        chatService.on('disconnect', onDisconnect);
        chatService.on('message', onMessage);
        
        // Établir la connexion
        console.log('[Messages] Tentative de connexion WebSocket...');
        await chatService.connect(user.token);
        
        // Nettoyage lors du démontage
        return () => {
          console.log('[Messages] Nettoyage des écouteurs WebSocket');
          chatService.off('connect', onConnect);
          chatService.off('error', onError);
          chatService.off('disconnect', onDisconnect);
          chatService.off('message', onMessage);
        };
      } catch (error) {
        console.error('[Messages] Échec de la connexion WebSocket:', error);
        showError('Impossible de se connecter au chat. Veuillez réessayer.');
        if (isMounted) setIsConnected(false);
      }
    };

    initializeChat();

    return () => {
      isMounted = false;
    };
  }, [user?.token]);

  // Vérification de l'authentification
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirection si l'utilisateur n'est pas authentifié
  if (!user) {
    return <Navigate to="/login" state={{ from: '/demenageur/messages' }} replace />;
  }

  // Vérification du rôle
  if (user.role !== 'demenageur') {
    console.warn('[Messages] Redirection - L\'utilisateur n\'est pas un déménageur', { 
      userRole: user.role || 'inconnu'
    });
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Messages</h1>
        <p className="text-[#64748B] mt-1">Discutez avec vos clients et confirmez les détails.</p>
      </div>
      
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <ChatInterface />
      </div>
    </div>
  );
};

export default Messages;
